import { usePageSEO } from "@/lib/use-page-seo";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import HowItWorks from "@/components/HowItWorks";
import FAQSection from "@/components/FAQSection";
import MegaFooter from "@/components/MegaFooter";
import ObfuscatedEmail from "@/components/ObfuscatedEmail";

const COUNTRIES = [
  { name: "Australia", flag: "🇦🇺", group: "H", notes: "Australia are in Group H. Matches kick off in Australian evening/overnight times (AEST). Set your calendar app to refresh hourly before group stage matches." },
  { name: "United States", flag: "🇺🇸", group: "A", notes: "The USA co-host the tournament and are in Group A. All USA group stage matches are played on home soil in American stadiums. Kickoff times are in US Eastern, Central or Pacific time depending on city." },
  { name: "Canada", flag: "🇨🇦", group: "B", notes: "Canada co-host and are in Group B. Matches played in Canadian cities start in local time — Toronto, Vancouver and others. An easy one to add for Canadian fans." },
  { name: "Mexico", flag: "🇲🇽", group: "B", notes: "Mexico are co-hosts in Group B. Several matches are played in Mexican stadiums including Mexico City and Guadalajara. Local kickoff times apply." },
  { name: "England", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", group: "C", notes: "England are in Group C. Matches kick off in US time zones — typically late evening or overnight UK time. Add the calendar and set notifications so you don't miss early-morning kickoffs." },
  { name: "Brazil", flag: "🇧🇷", group: "E", notes: "Brazil are in Group E. Matches kick off in North American time zones, which translates to afternoon or early evening in Brazil (BRT)." },
  { name: "Argentina", flag: "🇦🇷", group: "F", notes: "Argentina, defending champions, are in Group F. Check local kickoff times (ART) — most group stage matches will be afternoon or evening in Argentina." },
  { name: "Germany", flag: "🇩🇪", group: "G", notes: "Germany are in Group G. North American kickoff times typically mean evening (CEST) or late-night (CET) in Germany. Worth setting calendar reminders." },
  { name: "France", flag: "🇫🇷", group: "D", notes: "France are in Group D. North American kickoff times land in evening or late night (CEST/CET) for French fans." },
  { name: "Spain", flag: "🇪🇸", group: "I", notes: "Spain are in Group I. Kickoffs in North America are typically evening or late night in Spain (CEST/CET)." },
];

export default function GuidePage() {
  usePageSEO("meta.guide_title", "/guide");
  const lp = useLocalePath();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              ← Back
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Page header */}
        <div className="mb-12">
          <h1
            className="text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            How to Add the World Cup 2026 to Your Calendar
          </h1>
          <p className="text-muted-foreground text-base max-w-2xl leading-relaxed">
            The FIFA World Cup 2026 runs from June 11 to July 19, spanning the USA, Canada and Mexico. 48 teams, 104 matches, 16 host cities. Here's how to get every match for your teams into Apple Calendar, Google Calendar or Outlook — live-updating, free for your first team.
          </p>
        </div>

        {/* Tournament overview */}
        <section className="mb-10 p-6 rounded-xl border border-border/50 bg-card/40">
          <h2
            className="text-2xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Tournament at a glance
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            {[
              { label: "Start date", value: "11 June 2026" },
              { label: "Final", value: "19 July 2026" },
              { label: "Teams", value: "48 nations" },
              { label: "Matches", value: "104 total" },
              { label: "Host countries", value: "USA, Canada, Mexico" },
              { label: "Host cities", value: "16 cities" },
              { label: "Format", value: "Group stage + knockouts" },
              { label: "Calendar updates", value: "Automatic, hourly" },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg border border-border/30 bg-background/40 p-3">
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className="font-semibold text-foreground text-sm">{value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Live vs static */}
        <section className="mb-10 p-6 rounded-xl border border-border/50 bg-card/40">
          <h2
            className="text-2xl font-bold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Live subscription vs. one-time download
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-muted-foreground">
            <div>
              <p className="font-semibold text-foreground mb-2">Static .ics download</p>
              <ul className="space-y-1">
                <li>• Stale immediately — kickoff times can change</li>
                <li>• Knockout opponents show as TBD forever</li>
                <li>• You have to download a new file each round</li>
                <li>• No scores, no venue updates</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-2">WC Calendar live subscription</p>
              <ul className="space-y-1">
                <li>• Refreshes automatically in your calendar app</li>
                <li>• Knockout teams fill in as the draw is made</li>
                <li>• Rescheduled kickoffs update on your phone</li>
                <li>• Scores appear in calendar entries after matches</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Step-by-step per app */}
        <section className="mb-10">
          <h2
            className="text-3xl font-bold uppercase tracking-tight mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Step-by-step setup
          </h2>
          <div className="space-y-6">
            {[
              {
                app: "Apple Calendar (iPhone, iPad & Mac)",
                steps: [
                  "Go to wccalendar.com and pick your team(s). Your first team is free.",
                  "Enter your email and tap Get Calendar.",
                  "Check your email and tap the subscription link.",
                  "iOS shows 'Subscribe to Calendar?' — tap Subscribe.",
                  "Done. Matches appear immediately and update automatically.",
                ],
                note: "On Mac: File → New Calendar Subscription, paste the ICS URL, set Auto-refresh to 'Every hour'.",
              },
              {
                app: "Google Calendar (Android & Web)",
                steps: [
                  "Go to wccalendar.com, pick your team(s), and copy your ICS URL.",
                  "Open Google Calendar in a browser (not the mobile app).",
                  "Click + next to 'Other calendars' → 'From URL'.",
                  "Paste your ICS URL and click 'Add calendar'.",
                  "Google refreshes the feed every 6–24 hours automatically.",
                ],
                note: "Google Calendar controls its own refresh rate. For knockout rounds, check the web version for the latest if the app hasn't updated yet.",
              },
              {
                app: "Microsoft Outlook",
                steps: [
                  "Get your ICS URL from wccalendar.com.",
                  "In Outlook Calendar: on Windows go to Home → Open Calendar → From Internet. On Mac: File → New Calendar Subscription.",
                  "Paste your ICS URL and click OK.",
                  "Outlook adds the calendar and syncs on its own schedule.",
                ],
                note: null,
              },
            ].map(({ app, steps, note }) => (
              <div key={app} className="rounded-xl border border-border/50 bg-card/40 p-5">
                <h3
                  className="text-lg font-bold uppercase mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {app}
                </h3>
                <ol className="space-y-2 mb-3">
                  {steps.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                {note && (
                  <p className="text-xs text-muted-foreground border-l-2 border-border/50 pl-3 italic">{note}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Country-specific section */}
        <section className="mb-10">
          <h2
            className="text-3xl font-bold uppercase tracking-tight mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Your team's schedule
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            Select your country on wccalendar.com to get a personalised feed with only your team's matches. Here's what to expect for some of the bigger nations.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COUNTRIES.map((c) => (
              <div key={c.name} className="rounded-xl border border-border/50 bg-card/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{c.flag}</span>
                  <div>
                    <h3
                      className="text-base font-bold uppercase leading-tight"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      {c.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">Group {c.group}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.notes}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mb-12 p-6 rounded-xl border border-primary/20 bg-primary/5 text-center">
          <h2
            className="text-2xl font-bold uppercase tracking-tight mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Get your free calendar
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            First team free. Set up in 30 seconds. Works with Apple, Google and Outlook.
          </p>
          <a
            href={lp("/")}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Pick your teams →
          </a>
        </section>

        <HowItWorks />
        <FAQSection />
      </main>

      <MegaFooter />

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <WCLogo compact />
            <span>© {new Date().getFullYear()} Skella &amp; Co</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={lp("/faq")} className="hover:text-foreground transition-colors">FAQ & Privacy</a>
            <ObfuscatedEmail />
          </div>
        </div>
      </footer>
    </div>
  );
}
